import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Star, Users, Award, ArrowRight, Phone, Mail, MapPin, Search, Filter, TrendingUp, Shield, Clock } from 'lucide-react';
import { VehicleGrid, FeaturedVehicleCard } from '../components/VehicleCard';
import { Carousel } from '../components/Carousel';
import { vehicleService } from '../services/api';
import logoImage from '../assets/images/logo-avenida-veiculos.png';
import bannerImage from '../assets/images/Banner.jpeg';

const WHATSAPP_URL = 'https://wa.me/5551985521710';

export const Home = () => {
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickFilters, setQuickFilters] = useState({
    brand: '',
    maxPrice: '',
    fuelType: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehiclesResponse, statsResponse] = await Promise.all([
          vehicleService.getFeaturedVehicles(8),
          vehicleService.getStatistics()
        ]);
        
        setFeaturedVehicles(vehiclesResponse.data);
        setStatistics(statsResponse.data);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Dados do carrossel
  const carouselSlides = [
    {
      image: bannerImage,
      badge: 'Novos Veículos',
      title: 'Encontre o Carro dos Seus Sonhos',
      subtitle: 'Melhores veículos seminovos e usados com qualidade garantida',
      primaryButton: {
        text: 'Ver Estoque',
        href: '/inventory'
      },
      secondaryButton: {
        text: 'Fale Conosco',
        href: '/contact'
      }
    },
    {
      badge: 'Garantia',
      title: 'Qualidade Garantida',
      subtitle: 'Todos os nossos veículos passam por rigorosa inspeção técnica',
      primaryButton: {
        text: 'Conheça Nossa Garantia',
        href: WHATSAPP_URL
      },
      secondaryButton: {
        text: 'Ver Estoque',
        href: '/inventory'
      }
    }
  ];

  const features = [
    {
      icon: <Car className="h-8 w-8" />,
      title: "Veículos Selecionados",
      description: "Todos os nossos veículos passam por rigorosa inspeção técnica"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Garantia de Qualidade",
      description: "Oferecemos garantia estendida em todos os veículos"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Atendimento Personalizado",
      description: "Equipe especializada para te ajudar a encontrar o carro ideal"
    },
  ];

  const stats = [
    { label: 'Veículos Cadastrados', value: statistics?.totalVehicles || 0, icon: <Car className="h-6 w-6" /> },
    { label: 'Disponíveis', value: statistics?.availableVehicles || 0, icon: <TrendingUp className="h-6 w-6" /> },
    { label: 'Vendidos', value: '1000+', icon: <Award className="h-6 w-6" /> },
    { label: 'Anos de Experiência', value: '30+', icon: <Clock className="h-6 w-6" /> }
  ];

  const handleQuickFilterChange = (field, value) => {
    setQuickFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuickSearch = () => {
    const params = new URLSearchParams();

    if (quickFilters.brand) {
      const brandFormatted =
        quickFilters.brand.charAt(0).toUpperCase() + quickFilters.brand.slice(1);
      params.set('brand', brandFormatted);
    }

    if (quickFilters.maxPrice) {
      params.set('maxPrice', quickFilters.maxPrice);
    }

    if (quickFilters.fuelType) {
      params.set('fuelType', quickFilters.fuelType);
    }

    const queryString = params.toString();
    navigate(queryString ? `/inventory?${queryString}` : '/inventory');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Hero Section com Carrossel */}
      <section className="relative">
        <Carousel slides={carouselSlides} />
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-white dark:bg-dark-800">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-dark-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Por Que Escolher a Avenida Veículos?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Somos especialistas em veículos seminovos e usados, oferecendo 
              qualidade, transparência e o melhor atendimento.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
            {features.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300 group-hover:scale-110">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles Section */}
      <section className="py-20 bg-white dark:bg-dark-800">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Veículos em Destaque
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Confira nossa seleção especial de veículos com as melhores condições.
            </p>
          </div>
          
          <VehicleGrid vehicles={featuredVehicles} loading={loading} />
          
          <div className="text-center mt-12">
            <Link 
              to="/inventory" 
              className="btn btn-primary btn-lg inline-flex items-center"
            >
              Ver Todo o Estoque
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-20 bg-gradient-primary">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Encontre Seu Veículo Ideal
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Use nossos filtros avançados para encontrar exatamente o que você procura.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="filter-group">
                  <label className="filter-label">Marca</label>
                  <select
                    className="select"
                    value={quickFilters.brand}
                    onChange={(e) => handleQuickFilterChange('brand', e.target.value)}
                  >
                    <option value="">Todas as marcas</option>
                    <option value="toyota">Toyota</option>
                    <option value="honda">Honda</option>
                    <option value="volkswagen">Volkswagen</option>
                    <option value="ford">Ford</option>
                    <option value="chevrolet">Chevrolet</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label className="filter-label">Preço até</label>
                  <select
                    className="select"
                    value={quickFilters.maxPrice}
                    onChange={(e) => handleQuickFilterChange('maxPrice', e.target.value)}
                  >
                    <option value="">Qualquer preço</option>
                    <option value="50000">R$ 50.000</option>
                    <option value="100000">R$ 100.000</option>
                    <option value="150000">R$ 150.000</option>
                    <option value="200000">R$ 200.000</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label className="filter-label">Combustível</label>
                  <select
                    className="select"
                    value={quickFilters.fuelType}
                    onChange={(e) => handleQuickFilterChange('fuelType', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="flex">Flex</option>
                    <option value="gasolina">Gasolina</option>
                    <option value="diesel">Diesel</option>
                    <option value="eletrico">Elétrico</option>
                  </select>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  className="btn btn-primary flex-1"
                  type="button"
                  onClick={handleQuickSearch}
                >
                  <Search className="h-5 w-5 mr-2" />
                  Buscar Veículos
                </button>
                <Link to="/inventory" className="btn btn-outline flex-1">
                  <Filter className="h-5 w-5 mr-2" />
                  Filtros Avançados
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-dark-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Pronto para Encontrar Seu Próximo Veículo?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Entre em contato conosco e agende uma visita. Nossa equipe está 
              pronta para te ajudar a encontrar o carro perfeito.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 text-primary rounded-2xl mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <Phone className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Telefone</h3>
              <p className="text-gray-300">(51) 99521-2121</p>
              <p className="text-gray-300">(51) 99520-7441</p>
              <p className="text-gray-300">(51) 98554-3345</p>
            </div> 
            
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 text-primary rounded-2xl mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Email</h3>
              <p className="text-gray-300">Avenidaveiculos.rs@gmail.com</p>
              <p className="text-gray-300">Avenidaveiculos.rs@terra.com.br</p>
            </div>
            
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 text-primary rounded-2xl mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Endereço</h3>
              <p className="text-gray-300">Avenida João Pereira de Vargas, 132</p>
              <p className="text-gray-300">Centro - Sapucaia do Sul/RS</p>
            </div>
          </div>
          
          <div className="text-center">
            <Link 
              to="/contact" 
              className="btn btn-primary btn-lg"
            >
              Entre em Contato
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
